import type { Area } from "react-easy-crop";

const MAX_CANVAS_AREA = 16777216;

const createImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", () =>
      reject(new Error(`Failed to load image: ${url}`)),
    );
    image.setAttribute("crossOrigin", "anonymous");
    image.src = url;
  });

const getOriginScale = (width: number, height: number): number => {
  let scale = 1;
  while (width * scale * height * scale > MAX_CANVAS_AREA) {
    scale /= 2;
  }
  return scale;
};

export interface ICropImageOptions {
  format?: "jpeg" | "png";
  maxWidth?: number;
  backgroundColor?: string;
}

/**
 * Crops `imageSrc` to `pixelCrop` and returns a data URL. When the source
 * doesn't fully cover the crop window (portrait photo into landscape cover,
 * etc.), `backgroundColor` fills the empty area so a `jpeg` output doesn't
 * fall back to black where the canvas would otherwise be transparent.
 */
export const cropImageWithBackground = async (
  imageSrc: string,
  pixelCrop: Area,
  { format = "jpeg", maxWidth = 1024, backgroundColor }: ICropImageOptions = {},
): Promise<string | null> => {
  const image = await createImage(imageSrc);
  const scratch = document.createElement("canvas");
  const scratchCtx = scratch.getContext("2d");
  const output = document.createElement("canvas");
  const outputCtx = output.getContext("2d");
  if (!scratchCtx || !outputCtx) return null;

  const originScale = getOriginScale(image.width, image.height);
  scratch.width = image.width * originScale;
  scratch.height = image.height * originScale;
  scratchCtx.drawImage(
    image,
    0,
    0,
    image.width,
    image.height,
    0,
    0,
    scratch.width,
    scratch.height,
  );

  output.width = pixelCrop.width * originScale;
  output.height = pixelCrop.height * originScale;

  if (backgroundColor) {
    outputCtx.fillStyle = backgroundColor;
    outputCtx.fillRect(0, 0, output.width, output.height);
  }

  outputCtx.drawImage(
    scratch,
    -pixelCrop.x * originScale,
    -pixelCrop.y * originScale,
  );

  if (output.width <= maxWidth) {
    return output.toDataURL(`image/${format}`);
  }

  const resized = document.createElement("canvas");
  const resizedCtx = resized.getContext("2d");
  if (!resizedCtx) return null;
  resized.width = maxWidth;
  resized.height = Math.round(maxWidth * (pixelCrop.height / pixelCrop.width));

  if (backgroundColor) {
    resizedCtx.fillStyle = backgroundColor;
    resizedCtx.fillRect(0, 0, resized.width, resized.height);
  }

  resizedCtx.drawImage(
    output,
    0,
    0,
    output.width,
    output.height,
    0,
    0,
    resized.width,
    resized.height,
  );

  return resized.toDataURL(`image/${format}`);
};
